import { Transaction } from '../types';
import { parseCsv } from '../importers';
import { getIntelligentCSVMapping } from '../services/geminiService';

export interface AiCsvMapping {
    date?: string | null;
    action?: string | null;
    symbol?: string | null;
    quantity?: string | null;
    price?: string | null;
    amount?: string | null;
    currency?: string | null;
}

const parseCsvLine = (text: string) => {
    const result: string[] = [];
    let current = '';
    let inQuote = false;

    for (let i = 0; i < text.length; i++) {
        const char = text[i];
        if (char === '"') {
            inQuote = !inQuote;
        } else if (char === ',' && !inQuote) {
            result.push(current.trim());
            current = '';
        } else {
            current += char;
        }
    }

    result.push(current.trim());
    return result.map((val) => val.replace(/^"|"$/g, ''));
};

const cleanNumber = (value?: string) => {
    if (!value) return 0;
    const cleanStr = value.replace(/,/g, '').replace(/[£$€]/g, '');
    const val = parseFloat(cleanStr);
    return isNaN(val) ? 0 : val;
};

const findIndex = (headers: string[], target?: string | null) => {
    if (!target) return -1;
    const normalized = target.trim().toLowerCase();
    return headers.findIndex((h) => h.trim().toLowerCase() === normalized);
};

const normalizeAction = (raw: string): Transaction['action'] | null => {
    const text = raw.toLowerCase();
    if (text.includes('buy')) return 'Buy';
    if (text.includes('sell')) return 'Sell';
    if (text.includes('dividend') || text.includes('income') || text.includes('interest')) return 'Dividend';
    if (text.includes('deposit') || text.includes('top up') || text.includes('transfer in')) return 'Deposit';
    if (text.includes('withdraw')) return 'Withdrawal';
    return null;
};

const parseDate = (raw?: string): string | null => {
    if (!raw) return null;
    const cleaned = raw.trim();
    if (!cleaned) return null;

    const isoAttempt = new Date(cleaned.replace(' ', 'T'));
    if (!isNaN(isoAttempt.getTime())) return isoAttempt.toISOString();

    const normalized = cleaned.replace(/[-.]/g, '/');
    const [datePart, timePart] = normalized.split(' ');
    const [a, b, c] = datePart.split('/');
    if (a && b && c) {
        const isYearFirst = a.length === 4;
        const day = isYearFirst ? c : a;
        const month = b;
        const year = isYearFirst ? a : c;
        const isoStr = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${timePart || '00:00:00'}`;
        const parsed = new Date(isoStr);
        if (!isNaN(parsed.getTime())) return parsed.toISOString();
    }

    return null;
};

export const parseTransactionsWithMapping = (csvText: string, mapping: AiCsvMapping, sourceLabel = 'CSV import'): Transaction[] => {
    const lines = csvText.split(/\r?\n/).filter((l) => l.trim().length > 0);
    if (lines.length < 2) return [];

    const headers = parseCsvLine(lines[0]);
    const idxDate = findIndex(headers, mapping.date);
    const idxAction = findIndex(headers, mapping.action);
    const idxSymbol = findIndex(headers, mapping.symbol);
    const idxQuantity = findIndex(headers, mapping.quantity);
    const idxPrice = findIndex(headers, mapping.price);
    const idxAmount = findIndex(headers, mapping.amount);
    const idxCurrency = findIndex(headers, mapping.currency);

    const parsed: Transaction[] = [];

    for (let i = 1; i < lines.length; i++) {
        const cols = parseCsvLine(lines[i]);
        const rawDate = idxDate >= 0 ? cols[idxDate] : cols[0];
        const rawAction = idxAction >= 0 ? cols[idxAction] : cols[1];
        const rawSymbol = idxSymbol >= 0 ? cols[idxSymbol] : '';
        const quantity = cleanNumber(idxQuantity >= 0 ? cols[idxQuantity] : '');
        const price = cleanNumber(idxPrice >= 0 ? cols[idxPrice] : '');
        const amount = cleanNumber(idxAmount >= 0 ? cols[idxAmount] : '');
        const currency = idxCurrency >= 0 ? cols[idxCurrency] : 'GBP';

        const action = normalizeAction(rawAction || '');
        const date = parseDate(rawDate);
        const symbol = rawSymbol || 'CASH';

        if (!action || !date || !symbol) continue;

        const fallbackAmount = price && quantity ? price * quantity : amount;
        const finalAmount = fallbackAmount || amount;

        const transaction: Transaction = {
            id: `${Date.now()}_${i}_${symbol}`.replace(/[^a-zA-Z0-9_]/g, ''),
            date,
            action,
            symbol,
            name: symbol,
            quantity,
            price,
            priceCurrency: currency || 'GBP',
            amount: finalAmount,
            currency: currency || 'GBP',
            source: 'csv',
            sourceLabel,
        };

        parsed.push(transaction);
    }

    return parsed;
};

export const smartParseTransactions = async (
    csvText: string,
    brokerName?: string
): Promise<{ transactions: Transaction[]; mapping: AiCsvMapping | null }> => {
    const lines = csvText.split(/\r?\n/).filter((l) => l.trim().length > 0);
    const sample = lines.slice(0, 8).join('\n');

    const mapping = await getIntelligentCSVMapping(`Broker: ${brokerName || 'Unknown'}\n${sample}`);
    if (!mapping) {
        const fallbackTransactions = parseCsv('custom', csvText);
        return { transactions: fallbackTransactions, mapping: null };
    }

    const transactions = parseTransactionsWithMapping(csvText, mapping, brokerName ? `${brokerName} CSV` : 'CSV import');
    if (transactions.length === 0) {
        const fallbackTransactions = parseCsv('custom', csvText);
        if (fallbackTransactions.length > 0) {
            return { transactions: fallbackTransactions, mapping: null };
        }
    }
    return { transactions, mapping };
};
